import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  createFilterOptions,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AutocompleteCloseReason, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import type { PaperProps } from '@mui/material/Paper';
import type { ClipboardEvent, InputHTMLAttributes, MouseEvent, SyntheticEvent } from 'react';
import { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FieldValues, RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import { filterOptionsWithAncestors } from '@/components/VNDropdown/types';
import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import {
  hasFieldHelpIcon,
  normalizeFieldHelpProps,
  useVNFieldHelpContext,
  VNFieldHelpIconButton,
  VNFieldHelpItemTipIcon,
  VNFieldHelpLayout,
} from '@/components/VNFieldHelp';

type AutocompleteInputProps = {
  params: AutocompleteRenderInputParams;
  label: string;
  required: boolean;
  error?: boolean;
  helperText?: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
};

const VNAutocompleteInput = ({ params, label, required, error, helperText, inputRef, inputProps }: AutocompleteInputProps) => {
  const { iconConfig } = useVNFieldHelpContext();
  const { InputProps: paramsInputProps, inputProps: paramsNativeInputProps, ...restParams } = params;
  const { onPaste: muiOnPaste, ...restMuiInputProps } = paramsNativeInputProps ?? {};
  const { onPaste: callerOnPaste, ...restCallerInputProps } = inputProps ?? {};
  const mergedParams = {
    ...restParams,
    inputProps: {
      ...restMuiInputProps,
      ...restCallerInputProps,
      ...(callerOnPaste || muiOnPaste
        ? {
            onPaste: (e: ClipboardEvent<HTMLInputElement>) => {
              callerOnPaste?.(e);
              if (!e.defaultPrevented) {
                muiOnPaste?.(e);
              }
            },
          }
        : {}),
    },
    InputProps: {
      ...paramsInputProps,
      endAdornment: iconConfig ? (
        <>
          {paramsInputProps.endAdornment}
          <InputAdornment position="end" className="vn-field-help__input-adornment">
            <VNFieldHelpIconButton {...iconConfig} />
          </InputAdornment>
        </>
      ) : (
        paramsInputProps.endAdornment
      ),
    },
  };

  return (
    <TextField
      {...mergedParams}
      label={`${label}${required ? ' *' : ''}`}
      size="small"
      error={error}
      helperText={helperText}
      inputRef={inputRef}
    />
  );
};

type Props = VNFieldHelpProps & {
  name: string;
  label: string;
  labelProp?: string;
  valueProp?: string;
  options?: any[];
  hideOptions?: boolean;
  multiple?: boolean;
  rules?: Omit<RegisterOptions<FieldValues, string>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  required?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  onChange?: (value: any) => void;
  optionTemplate?: (option: any) => React.ReactNode;
  inputValue?: string;
  onInputChange?: (event: SyntheticEvent, value: string, reason: string) => void;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  allowCustom?: boolean;
  chipTooltip?: boolean;
  chipTooltipArrow?: boolean;
  chipTooltipEnterDelay?: number;
  chipTooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  loading?: boolean;
  showLoadingFooter?: boolean;
  loadingFooterText?: string;
  /** Extra class(es) on multi-select chips (e.g. `tag-chip cat` for category color). */
  chipClassName?: string;
  /**
   * Multi-select only. Omit for always-expanded chip row (all chips).
   * Pass a number (e.g. `20`) for first N chips + clickable "+N" to expand. `-1` shows all in compact row.
   */
  limitTags?: number;
  /** Show checkbox adornments in the dropdown list (multi only). Default true. */
  showOptionCheckboxes?: boolean;
  /** `inset` puts search in the dropdown panel (like VNDropdown); default filters in the trigger field. */
  searchable?: 'default' | 'inset';
  /** Fired when the options panel closes (e.g. commit multi-select on blur). */
  onDropdownClose?: () => void;
};

const VNAutocomplete = ({
  name,
  label,
  labelProp = 'label',
  valueProp = 'value',
  options = [],
  hideOptions = false,
  multiple = false,
  rules,
  required = false,
  disabled = false,
  allowClear = false,
  onChange = null,
  optionTemplate,
  inputValue,
  onInputChange,
  inputProps,
  allowCustom = false,
  chipTooltip = true,
  chipTooltipArrow = true,
  chipTooltipEnterDelay = 500,
  chipTooltipPlacement = 'top',
  loading = false,
  showLoadingFooter = false,
  loadingFooterText = 'Loading more results...',
  chipClassName,
  limitTags,
  showOptionCheckboxes = true,
  searchable = 'default',
  onDropdownClose = null,
  ...fieldHelpProps
}: Props) => {
  const { control, watch } = useFormContext();
  const insetSearch = searchable === 'inset';
  const [insetQuery, setInsetQuery] = useState('');
  const [insetListOpen, setInsetListOpen] = useState(false);
  const [userExpandedChips, setUserExpandedChips] = useState(false);
  const insetSearchRef = useRef<HTMLInputElement>(null);
  const popperPaperRef = useRef<HTMLDivElement | null>(null);
  const insetSearchApiRef = useRef({
    query: '',
    setQuery: (_value: string) => {},
    pendingFocus: false,
  });
  const suppressInsetCloseUntilRef = useRef(0);
  const insetListOpenRef = useRef(false);
  const insetSearchInputId = useId();
  const paperExtrasRef = useRef({
    insetSearch: false,
    showLoadingFooter: false,
    loading: false,
    loadingFooterText: 'Loading more results...',
    insetSearchInputId: '',
  });

  insetSearchApiRef.current.query = insetQuery;
  insetSearchApiRef.current.setQuery = setInsetQuery;
  insetListOpenRef.current = insetListOpen;
  paperExtrasRef.current = {
    insetSearch,
    showLoadingFooter,
    loading,
    loadingFooterText,
    insetSearchInputId,
  };

  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRootRef = useRef<HTMLDivElement | null>(null);

  const focusInsetSearchInput = useCallback(() => {
    const tryFocus = (attempt = 0) => {
      if (!insetListOpenRef.current) {
        return;
      }
      const el = insetSearchRef.current;
      if (el) {
        el.focus({ preventScroll: true });
        if (document.activeElement === el) {
          insetSearchApiRef.current.pendingFocus = false;
          return;
        }
      }
      if (attempt < 10) {
        window.setTimeout(() => tryFocus(attempt + 1), attempt < 2 ? 0 : 16);
      }
    };
    tryFocus();
  }, []);

  const handleInsetAutocompleteClose = useCallback(
    (event: SyntheticEvent, reason: AutocompleteCloseReason) => {
      if (Date.now() < suppressInsetCloseUntilRef.current && (reason === 'blur' || reason === 'toggleInput')) {
        focusInsetSearchInput();
        return;
      }
      if (reason === 'blur') {
        const related = (event as React.FocusEvent).relatedTarget;
        if (related instanceof Node && popperPaperRef.current?.contains(related)) {
          return;
        }
        // Popup/clear/chips live in the trigger — blur to them must not close (e.g. opening via chevron).
        if (related instanceof Node && autocompleteRootRef.current?.contains(related)) {
          if (insetListOpenRef.current) {
            focusInsetSearchInput();
          }
          return;
        }
        // MUI returns focus to the trigger input after each multi-select toggle — keep inset search focused.
        if (related === inputRef.current || (related instanceof Node && inputRef.current?.contains(related))) {
          focusInsetSearchInput();
          return;
        }
      }
      setInsetListOpen(false);
      setInsetQuery('');
      insetListOpenRef.current = false;
      onDropdownClose?.();
    },
    [focusInsetSearchInput, onDropdownClose]
  );

  const assignInsetSearchRef = useCallback((node: HTMLInputElement | null) => {
    insetSearchRef.current = node;
    if (node && insetListOpenRef.current && insetSearchApiRef.current.pendingFocus) {
      insetSearchApiRef.current.pendingFocus = false;
      node.focus({ preventScroll: true });
    }
  }, []);

  const handleInsetOpen = useCallback(() => {
    suppressInsetCloseUntilRef.current = Date.now() + 250;
    insetSearchApiRef.current.pendingFocus = true;
    insetListOpenRef.current = true;
    setInsetListOpen(true);
    focusInsetSearchInput();
  }, [focusInsetSearchInput]);

  const normalized = normalizeFieldHelpProps(fieldHelpProps);
  const { dropDownItemHelperTooltip, dropDownItemHelperText } = normalized;
  const showFieldHelp = hasFieldHelpIcon(fieldHelpProps);
  const hasItemHelp = Boolean(
    (dropDownItemHelperTooltip && Object.keys(dropDownItemHelperTooltip).length > 0) ||
      (dropDownItemHelperText && Object.keys(dropDownItemHelperText).length > 0)
  );

  const formValue = watch(name);
  const selectedValues: string[] | string = (() => {
    if (multiple) {
      return Array.isArray(formValue) ? formValue : [];
    }
    return (formValue as string) || '';
  })();
  const selectedCount = Array.isArray(selectedValues) ? selectedValues.length : 0;
  const visibleChipCountInTrigger = (() => {
    if (!multiple || selectedCount === 0) {
      return 0;
    }
    if (limitTags === undefined || userExpandedChips) {
      return selectedCount;
    }
    if (limitTags < 0) {
      return selectedCount;
    }
    return Math.min(selectedCount, limitTags);
  })();
  const chipsExpandedLayout = multiple && visibleChipCountInTrigger > 1;

  const chipSelectionKey = useMemo(() => (Array.isArray(selectedValues) ? selectedValues.join('\0') : ''), [selectedValues]);

  useEffect(() => {
    setUserExpandedChips(false);
  }, [chipSelectionKey]);

  useEffect(() => {
    if (insetListOpen) {
      setUserExpandedChips(false);
    }
  }, [insetListOpen]);

  const optionValues = useMemo(() => {
    const predefinedValues =
      typeof options?.[0] === 'object' ? options.map((option) => option[valueProp] ?? option.value) : options;

    return Array.from(new Set([...predefinedValues, ...(Array.isArray(selectedValues) ? selectedValues : [selectedValues])]));
  }, [options, selectedValues, valueProp]);

  const optionLabels = useMemo(() => {
    const predefinedLabels =
      typeof options?.[0] === 'object'
        ? Object.fromEntries(options.map((option) => [option[valueProp] ?? option.value, option[labelProp] ?? option.label]))
        : Object.fromEntries(options.map((option) => [option, option]));

    if (Array.isArray(selectedValues)) {
      selectedValues.forEach((val) => {
        if (!predefinedLabels[val]) {
          predefinedLabels[val] = val;
        }
      });
    } else if (selectedValues && !predefinedLabels[selectedValues]) {
      predefinedLabels[selectedValues] = selectedValues;
    }

    return predefinedLabels;
  }, [options, selectedValues, labelProp, valueProp]);

  const optionsByValue = useMemo(() => {
    if (!optionTemplate || typeof options?.[0] !== 'object') {
      return {};
    }
    return Object.fromEntries(options.map((option) => [option[valueProp] ?? option.value, option]));
  }, [options, optionTemplate, valueProp]);

  const defaultFilterOptions = useMemo(
    () =>
      createFilterOptions({
        matchFrom: 'any',
        stringify: (option: any) => optionLabels[option] || '',
      }),
    [optionLabels]
  );

  const dropdownOptionsForFilter = useMemo(() => {
    if (!options?.length || typeof options[0] !== 'object') {
      return [];
    }
    return options.map((option) => ({
      ...option,
      id: option[valueProp] ?? option.value,
      label: String(option[labelProp] ?? option.label ?? option[valueProp] ?? ''),
    }));
  }, [options, labelProp, valueProp]);

  const filterOptionsFn = useCallback(
    (opts: typeof optionValues, state: Parameters<typeof defaultFilterOptions>[1]) => {
      if (hideOptions) {
        return [];
      }
      if (!insetSearch) {
        return defaultFilterOptions(opts, state);
      }
      const q = insetQuery.trim();
      if (!q) {
        return opts;
      }
      if (dropdownOptionsForFilter.length > 0) {
        const filtered = filterOptionsWithAncestors(dropdownOptionsForFilter, insetQuery);
        const allowed = new Set(filtered.map((o) => o.id));
        return opts.filter((v) => allowed.has(v));
      }
      const lower = q.toLowerCase();
      return opts.filter((v) => (optionLabels[v] ?? String(v)).toLowerCase().includes(lower));
    },
    [defaultFilterOptions, dropdownOptionsForFilter, hideOptions, insetQuery, insetSearch, optionLabels]
  );

  const insetSelectionFocusKey = useMemo(
    () => (insetSearch && multiple ? JSON.stringify(selectedValues) : ''),
    [insetSearch, multiple, selectedValues]
  );

  useLayoutEffect(() => {
    if (!insetSearch || !insetListOpen) {
      return undefined;
    }
    insetSearchApiRef.current.pendingFocus = true;
    focusInsetSearchInput();
    return undefined;
  }, [focusInsetSearchInput, insetListOpen, insetSearch, insetSelectionFocusKey]);

  useEffect(() => {
    const insetPanelOpen = insetSearch && insetListOpen;
    if (!insetPanelOpen && !userExpandedChips) {
      return undefined;
    }
    const onDocMouseDown = (e: MouseEvent) => {
      const { target } = e;
      if (!(target instanceof Node)) {
        return;
      }
      if (popperPaperRef.current?.contains(target)) {
        return;
      }
      if (autocompleteRootRef.current?.contains(target)) {
        return;
      }
      if (insetPanelOpen) {
        setInsetListOpen(false);
        setInsetQuery('');
        insetListOpenRef.current = false;
      }
      if (userExpandedChips) {
        setUserExpandedChips(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [insetSearch, insetListOpen, userExpandedChips]);

  const PaperComponent = useMemo(() => {
    if (!showLoadingFooter && !insetSearch) {
      return undefined;
    }

    return forwardRef<HTMLDivElement, PaperProps>(function VnAutocompletePaper(props, ref) {
      const { children, className, ...paperProps } = props;
      const extras = paperExtrasRef.current;
      const api = insetSearchApiRef.current;

      const setPaperRef = (node: HTMLDivElement | null) => {
        popperPaperRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      };

      return (
        <Paper
          {...paperProps}
          ref={setPaperRef}
          className={[className, extras.insetSearch ? 'vn-autocomplete__paper--inset-search' : ''].filter(Boolean).join(' ')}
        >
          {extras.insetSearch ? (
            <div className="vn-dropdown__inset-search" role="presentation">
              <label htmlFor={extras.insetSearchInputId} className="vn-dropdown__inset-search-field">
                <SearchIcon className="vn-dropdown__inset-search-icon" fontSize="small" aria-hidden="true" />
                <input
                  ref={assignInsetSearchRef}
                  id={extras.insetSearchInputId}
                  type="text"
                  className="vn-dropdown__inset-search-input"
                  placeholder="Search..."
                  value={api.query}
                  onChange={(e) => api.setQuery(e.target.value)}
                  onMouseDown={(e) => e.preventDefault()}
                  autoComplete="off"
                  aria-label="Filter options"
                />
              </label>
            </div>
          ) : null}
          {children}
          {extras.showLoadingFooter && extras.loading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 1,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <CircularProgress size={14} />
              <Typography variant="caption">{extras.loadingFooterText}</Typography>
            </Box>
          ) : null}
        </Paper>
      );
    });
  }, [insetSearch, showLoadingFooter]);

  const renderOptionContent = (option: string | number) => {
    const displayLabel =
      optionTemplate && optionsByValue[option] ? optionTemplate(optionsByValue[option]) : optionLabels[option] ?? '';
    const key = String(option);
    const itemTooltip = dropDownItemHelperTooltip?.[key];
    const itemHelperText = dropDownItemHelperText?.[key];

    if (hasItemHelp) {
      return (
        <div className="vn-field-help-menu-item__content">
          <div className="vn-field-help-menu-item__main">
            <span className="vn-field-help-menu-item__label">{displayLabel}</span>
            {itemTooltip && <VNFieldHelpItemTipIcon tooltip={itemTooltip} />}
          </div>
          {itemHelperText && <span className="vn-field-help-menu-item__subtext">{itemHelperText}</span>}
        </div>
      );
    }

    return displayLabel;
  };

  const controller = (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? `${label} is required` : undefined,
        ...rules,
      }}
      render={({ field: { value, onChange: innerOnChange }, fieldState }) => {
        const hasValue = multiple ? Array.isArray(value) && value.length > 0 : value != null && value !== '';
        const autocompleteInputValueProps: { inputValue?: string } = {};
        if (!insetSearch && inputValue !== undefined) {
          autocompleteInputValueProps.inputValue = inputValue;
        }

        return (
          <Autocomplete
            disableCloseOnSelect={multiple}
            multiple={multiple}
            freeSolo={allowCustom || hideOptions}
            disableClearable={!allowClear || !hasValue}
            options={hideOptions ? [] : optionValues}
            disabled={disabled}
            {...(insetSearch
              ? {
                  open: insetListOpen,
                  onOpen: handleInsetOpen,
                  onClose: handleInsetAutocompleteClose,
                  componentsProps: {
                    popupIndicator: {
                      onMouseDown: (event: MouseEvent) => {
                        event.preventDefault();
                      },
                    },
                  },
                }
              : {
                  onClose: () => {
                    onDropdownClose?.();
                  },
                })}
            {...autocompleteInputValueProps}
            onInputChange={(event, newInputValue, reason) => {
              if (insetSearch && (reason === 'input' || reason === 'reset')) {
                return;
              }
              onInputChange?.(event, newInputValue, reason);

              if (allowCustom && !multiple && (reason === 'input' || reason === 'clear')) {
                innerOnChange(newInputValue);
              }
            }}
            value={(() => {
              if (multiple) {
                return Array.isArray(value) ? value : [];
              }
              if (Array.isArray(value)) {
                return value[0] ?? '';
              }
              return value || '';
            })()}
            loading={loading}
            onChange={(_, newValue) => {
              if (onChange) {
                onChange(newValue);
                if (insetSearch && multiple && insetListOpen) {
                  focusInsetSearchInput();
                }
                return;
              }
              if (!multiple) {
                innerOnChange(newValue);
              } else {
                const cleanedValue = Array.from(new Set(newValue.map((v: string) => v.trim()).filter(Boolean)));
                innerOnChange(cleanedValue);
                if (insetSearch && insetListOpen) {
                  focusInsetSearchInput();
                }
              }
            }}
            filterOptions={filterOptionsFn}
            ListboxProps={{
              className: 'vn-autocomplete__listbox',
              ...(insetSearch
                ? {
                    onMouseDown: (e: MouseEvent) => {
                      e.preventDefault();
                    },
                  }
                : {}),
            }}
            renderOption={(props, option, { selected }) => {
              const { key: reactKey, ...restProps } = props;
              return (
                <li
                  {...restProps}
                  key={reactKey}
                  className={[
                    restProps.className,
                    'vn-autocomplete__option',
                    multiple ? 'vn-autocomplete__option--multi' : '',
                    hasItemHelp ? 'vn-field-help-menu-item' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {multiple && showOptionCheckboxes && (
                    <Checkbox
                      checked={selected}
                      className="vn-dropdown-list__checkbox"
                      aria-hidden="true"
                      tabIndex={-1}
                      size="small"
                      disableRipple
                      onMouseDown={(e) => e.preventDefault()}
                    />
                  )}
                  {renderOptionContent(option)}
                </li>
              );
            }}
            renderTags={(tagValue, getTagProps) => {
              const showAllChips = limitTags === undefined || limitTags < 0 || userExpandedChips;
              const collapsedLimit = limitTags !== undefined && limitTags >= 0 ? limitTags : tagValue.length;
              const visibleCount = showAllChips ? tagValue.length : collapsedLimit;
              const visibleTags = tagValue.slice(0, visibleCount);
              const overflowCount =
                !showAllChips && limitTags !== undefined && limitTags >= 0 ? tagValue.length - visibleTags.length : 0;

              const chips = visibleTags.map((option, index) => {
                const tagLabel = optionLabels[option] ?? '';
                const chipRootClass = ['vn-chip', chipClassName].filter(Boolean).join(' ');
                const chip = (
                  <Chip
                    {...getTagProps({ index })}
                    classes={{ root: chipRootClass }}
                    size="small"
                    key={option}
                    label={tagLabel}
                    title={chipTooltip ? undefined : tagLabel || undefined}
                  />
                );

                if (!chipTooltip || !tagLabel) {
                  return chip;
                }

                return (
                  <Tooltip
                    key={`tooltip-${option}`}
                    title={tagLabel}
                    arrow={chipTooltipArrow}
                    placement={chipTooltipPlacement}
                    enterDelay={chipTooltipEnterDelay}
                  >
                    {chip}
                  </Tooltip>
                );
              });

              if (overflowCount > 0) {
                chips.push(
                  <span
                    className="vn-autocomplete__limit-badge"
                    key="vn-autocomplete-limit-tags"
                    role="button"
                    tabIndex={-1}
                    aria-label={`Show ${overflowCount} more selected`}
                    onMouseDown={(e: MouseEvent) => e.preventDefault()}
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation();
                      setUserExpandedChips(true);
                    }}
                  >
                    +{overflowCount}
                  </span>
                );
              }

              return chips;
            }}
            getOptionLabel={(option) => optionLabels[option] ?? String(option ?? '')}
            {...(PaperComponent ? { PaperComponent } : {})}
            renderInput={(params) => (
              <VNAutocompleteInput
                params={params}
                label={label}
                required={required}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={inputRef}
                inputProps={{
                  ...inputProps,
                  ...(insetSearch && !allowCustom && !hideOptions ? { readOnly: true } : {}),
                }}
              />
            )}
          />
        );
      }}
    />
  );

  return (
    <div
      className={[
        'vn-autocomplete',
        multiple ? 'vn-autocomplete--multiple' : '',
        chipsExpandedLayout ? 'vn-autocomplete--chips-expanded' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      ref={autocompleteRootRef}
    >
      {showFieldHelp ? (
        <VNFieldHelpLayout
          label={label}
          required={required}
          iconAttach="adornment"
          className="vn-field-help--autocomplete"
          {...fieldHelpProps}
        >
          {controller}
        </VNFieldHelpLayout>
      ) : (
        controller
      )}
    </div>
  );
};

export default VNAutocomplete;
